import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { ROUTES, COLORS, customToast } from '@common'
import { resetPasswordSchema, checkPasswordStrength, getPasswordStrengthClass, isTokenInvalidError } from '@utils'
import { ErrorTrackingAPI, membershipServices } from '@services'

const ResetPasswordPage = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [isLoading, setIsLoading] = useState(false)
    const [isVerifying, setIsVerifying] = useState(true)
    const [isTokenValid, setIsTokenValid] = useState(false)
    const [tokenError, setTokenError] = useState(null)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [passwordStrength, setPasswordStrength] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
    })

    // Get token and email from URL
    const token = searchParams.get('token')
    const encodedEmail = searchParams.get('email')
    let email = ''
    if (encodedEmail) {
        try {
            email = decodeURIComponent(encodedEmail)
        } catch (error) {
            ErrorTrackingAPI.sendError(`Failed to decode email: ${error.message}`, 'ResetPasswordPage', 'decodeEmail')
            // Set email to empty string to trigger error handling
            email = ''
        }
    }

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(resetPasswordSchema),
        defaultValues: {
            email: email,
            token: token || '',
            newPassword: '',
            confirmPassword: '',
        },
    })

    const newPassword = watch('newPassword')

    // Set form values when email/token are available
    useEffect(() => {
        if (email) {
            setValue('email', email)
        }
        if (token) {
            setValue('token', token)
        }
    }, [email, token, setValue])

    // Check password strength
    useEffect(() => {
        if (newPassword) {
            setPasswordStrength(checkPasswordStrength(newPassword))
        } else {
            setPasswordStrength({
                length: false,
                uppercase: false,
                lowercase: false,
                number: false,
            })
        }
    }, [newPassword])

    // Verify token on component mount
    useEffect(() => {
        const verifyToken = async () => {
            if (!token || !email) {
                setTokenError('Missing reset token or email. Please request a new password reset link.')
                setIsVerifying(false)
                setIsTokenValid(false)
                return
            }

            try {
                const result = await membershipServices.verifyPasswordResetToken({
                    email: email.toLowerCase().trim(),
                    token: token,
                })

                if (result.success) {
                    setIsTokenValid(true)
                    setTokenError(null)
                } else {
                    setIsTokenValid(false)
                    setTokenError(result.error || 'Invalid or expired reset token.')
                }
            } catch (error) {
                ErrorTrackingAPI.sendError(error, 'ResetPasswordPage', 'verifyToken')
                setIsTokenValid(false)
                setTokenError('Failed to verify reset token. Please try again.')
            } finally {
                setIsVerifying(false)
            }
        }

        verifyToken()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, email])

    const onSubmit = async data => {
        setIsLoading(true)

        try {
            const result = await membershipServices.resetPassword({
                email: data.email.toLowerCase().trim(),
                token: data.token,
                newPassword: data.newPassword,
            })

            if (result.success) {
                customToast.success('Password reset successfully! Please sign in with your new password.', {
                    duration: 4000,
                })
                // Navigate to login page after a short delay
                setTimeout(() => {
                    navigate(ROUTES.Membership.auth)
                }, 1500)
            } else {
                // Check if it's a token error
                if (isTokenInvalidError(result.error)) {
                    setTokenError(result.error)
                    setIsTokenValid(false)
                    customToast.error('Invalid or expired token. Please request a new password reset link.', {
                        duration: 5000,
                    })
                } else {
                    customToast.error(result.error || 'Failed to reset password. Please try again.', {
                        duration: 4000,
                    })
                }
            }
        } catch (error) {
            ErrorTrackingAPI.sendError(error, 'ResetPasswordPage', 'resetPassword')
            customToast.error('An error occurred. Please try again.', { duration: 3000 })
        } finally {
            setIsLoading(false)
        }
    }

    // Show loading state while verifying token
    if (isVerifying) {
        return (
            <div className="membership-auth-page">
                <div className="auth-container">
                    <div className="auth-header">
                        <div className="auth-icon"></div>
                        <h2>Verifying Reset Link</h2>
                        <p>Please wait while we verify your reset link...</p>
                    </div>
                    <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                        <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '2rem', color: COLORS.INFO?.text }}></i>
                    </div>
                </div>
            </div>
        )
    }

    // Show error if token is invalid
    if (!isTokenValid) {
        return (
            <div className="membership-auth-page">
                <div className="auth-container">
                    <div className="auth-header">
                        <div className="auth-icon"></div>
                        <h2>Invalid Reset Link</h2>
                        <p>The password reset link is invalid or has expired.</p>
                    </div>

                    {tokenError && (
                        <div
                            className="alert alert-danger"
                            style={{
                                marginTop: '1.5rem',
                                padding: '12px 16px',
                                backgroundColor: COLORS.ERROR?.background || '#f8d7da',
                                border: `1px solid ${COLORS.ERROR?.border || '#f5c6cb'}`,
                                borderRadius: '8px',
                                color: COLORS.ERROR?.text || '#721c24',
                                fontSize: '14px',
                            }}
                        >
                            <i className="bx bx-error-circle"></i> {tokenError}
                        </div>
                    )}

                    <div style={{ marginTop: '2rem' }}>
                        <button onClick={() => navigate(ROUTES.Membership.requestPasswordReset)} className="btn-submit">
                            Request New Reset Link
                        </button>
                    </div>

                    <div className="back-link" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                        <button
                            onClick={() => navigate(ROUTES.Membership.auth)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: COLORS.INFO?.text || '#0d6efd',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontSize: '14px',
                            }}
                        >
                            ← Back to Sign In
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // Show reset password form
    return (
        <div className="membership-auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <div className="auth-icon"></div>
                    <h2>Reset Password</h2>
                    <p>Enter your new password below.</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: '2rem' }}>
                    <div className="form-group">
                        <label htmlFor="newPassword">
                            <i className="bx bx-lock"></i> New Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                id="newPassword"
                                type={showPassword ? 'text' : 'password'}
                                {...register('newPassword')}
                                className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                                placeholder="Enter your new password"
                                autoComplete="new-password"
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontSize: '1.2rem',
                                }}
                            >
                                <i className={`bx ${showPassword ? 'bx-show' : 'bx-hide'}`}></i>
                            </button>
                        </div>
                        {errors.newPassword && <div className="invalid-feedback">{errors.newPassword.message}</div>}
                        {newPassword && (
                            <div className="password-strength" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                {
                                    Object.values(passwordStrength).every(Boolean) ?
                                        // All requirements met - show single success message
                                        <div
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.25rem',
                                                color: COLORS.SUCCESS.text,
                                            }}
                                        >
                                            <i
                                                className="bx bx-check-circle"
                                                style={{
                                                    color: COLORS.SUCCESS.text,
                                                    fontSize: '1rem',
                                                }}
                                            ></i>
                                            <span>All password requirements met</span>
                                        </div>
                                        // Show detailed requirements list
                                    :   <div className={`strength-indicator ${getPasswordStrengthClass(passwordStrength)}`}>
                                            <span
                                                className={passwordStrength.length ? 'valid' : 'invalid'}
                                                style={{
                                                    marginRight: '0.5rem',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                }}
                                            >
                                                <i
                                                    className={`bx ${passwordStrength.length ? 'bx-check-circle' : 'bx-x-circle'}`}
                                                    style={{
                                                        color: passwordStrength.length ? COLORS.SUCCESS.text : COLORS.ERROR.text,
                                                        fontSize: '1rem',
                                                    }}
                                                ></i>
                                                At least 8 characters
                                            </span>
                                            <span
                                                className={passwordStrength.uppercase ? 'valid' : 'invalid'}
                                                style={{
                                                    marginRight: '0.5rem',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                }}
                                            >
                                                <i
                                                    className={`bx ${passwordStrength.uppercase ? 'bx-check-circle' : 'bx-x-circle'}`}
                                                    style={{
                                                        color:
                                                            passwordStrength.uppercase ? COLORS.SUCCESS.text : COLORS.ERROR.text,
                                                        fontSize: '1rem',
                                                    }}
                                                ></i>
                                                One uppercase letter
                                            </span>
                                            <span
                                                className={passwordStrength.lowercase ? 'valid' : 'invalid'}
                                                style={{
                                                    marginRight: '0.5rem',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                }}
                                            >
                                                <i
                                                    className={`bx ${passwordStrength.lowercase ? 'bx-check-circle' : 'bx-x-circle'}`}
                                                    style={{
                                                        color:
                                                            passwordStrength.lowercase ? COLORS.SUCCESS.text : COLORS.ERROR.text,
                                                        fontSize: '1rem',
                                                    }}
                                                ></i>
                                                One lowercase letter
                                            </span>
                                            <span
                                                className={passwordStrength.number ? 'valid' : 'invalid'}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                }}
                                            >
                                                <i
                                                    className={`bx ${passwordStrength.number ? 'bx-check-circle' : 'bx-x-circle'}`}
                                                    style={{
                                                        color: passwordStrength.number ? COLORS.SUCCESS.text : COLORS.ERROR.text,
                                                        fontSize: '1rem',
                                                    }}
                                                ></i>
                                                One number
                                            </span>
                                        </div>

                                }
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">
                            <i className="bx bx-lock-alt"></i> Confirm Password
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                id="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                {...register('confirmPassword')}
                                className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                placeholder="Confirm your new password"
                                autoComplete="new-password"
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontSize: '1.2rem',
                                }}
                            >
                                <i className={`bx ${showConfirmPassword ? 'bx-show' : 'bx-hide'}`}></i>
                            </button>
                        </div>
                        {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword.message}</div>}
                    </div>

                    <button type="submit" className="btn-submit" disabled={isLoading}>
                        {isLoading ?
                            <>
                                <i className="bx bx-loader-alt bx-spin"></i> Resetting Password...
                            </>
                        :   'Reset Password'}
                    </button>
                </form>

                <div className="back-link" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    <button
                        onClick={() => navigate(ROUTES.Membership.auth)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: COLORS.INFO?.text || '#0d6efd',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '14px',
                        }}
                    >
                        ← Back to Sign In
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ResetPasswordPage
